/**
 * Semantic colour resolution for the viewer.
 *
 * Zone colours are read from the same design tokens the canvas and the report
 * theme resolve, so a zone class is the same colour everywhere it appears.
 * Reading them at build time rather than hard-coding component colours is what
 * keeps the viewer aligned with the central style guide.
 */

import { Color } from 'three';

import type { WindZoneClass } from '../../gen/ejot/roofing/v1/types';

/** Token names, so the mapping from zone class to token is stated once. */
export const ZONE_TOKENS: Record<WindZoneClass, string> = {
  WIND_ZONE_CLASS_UNSPECIFIED: '--zone-interior',
  WIND_ZONE_CLASS_INTERIOR: '--zone-interior',
  WIND_ZONE_CLASS_INNER_EDGE: '--zone-inner-border',
  WIND_ZONE_CLASS_EDGE: '--zone-edge',
  WIND_ZONE_CLASS_CORNER: '--zone-corner',
};

const ZONE_FALLBACKS: Record<WindZoneClass, string> = {
  WIND_ZONE_CLASS_UNSPECIFIED: '#527488',
  WIND_ZONE_CLASS_INTERIOR: '#527488',
  // Zone H shares the interior hue for now; the separate token is what makes
  // giving the inner border its own colour a one-line palette decision.
  WIND_ZONE_CLASS_INNER_EDGE: '#527488',
  WIND_ZONE_CLASS_EDGE: '#D63803',
  WIND_ZONE_CLASS_CORNER: '#CE1230',
};

export interface ViewerPalette {
  background: Color;
  buildingMass: Color;
  roofSurface: Color;
  outline: Color;
  dimension: Color;
  selection: Color;
  zone(zoneClass: WindZoneClass | undefined): Color;
}

/**
 * Reads the palette from the document's computed styles.
 *
 * The element is passed in rather than assumed to be the document root, so the
 * viewer resolves the tokens that actually apply to where it is mounted.
 */
export function readViewerPalette(element: Element): ViewerPalette {
  const styles = getComputedStyle(element);
  const read = (token: string, fallback: string): Color => {
    const value = styles.getPropertyValue(token).trim();
    return new Color(value || fallback);
  };

  const zones: Record<string, Color> = {};
  for (const [zoneClass, token] of Object.entries(ZONE_TOKENS)) {
    const typedClass = zoneClass as WindZoneClass;
    zones[zoneClass] = read(token, ZONE_FALLBACKS[typedClass]);
  }

  return {
    background: read('--viewer-scene-bg', '#F8F9FA'),
    buildingMass: read('--ejot-grey', '#BBBCBD'),
    roofSurface: read('--surface-muted', '#F1F1F1'),
    outline: read('--text-primary', '#000000'),
    dimension: read('--ejot-red', '#CE1230'),
    // Selection may share the brand-red hue used by the corner zone. Selection
    // remains unambiguous because the viewer also changes opacity and scale.
    selection: read('--ejot-red', '#CE1230'),
    zone(zoneClass) {
      return zones[zoneClass ?? 'WIND_ZONE_CLASS_UNSPECIFIED'] ?? zones.WIND_ZONE_CLASS_INTERIOR!;
    },
  };
}

/**
 * Reports whether two zone classes resolve to the same colour.
 *
 * Used by the tests that hold the rule: equal classifications share a colour,
 * and different ones do not.
 */
export function sameColour(a: Color, b: Color): boolean {
  return a.getHexString() === b.getHexString();
}
